import { autorun } from "mobx";
import { CountryWithStateModel } from "./CountryModels";
import { TaxStatus } from "../enums";
import {
  AuthService,
  CoreInputModelUtil,
  getNewErrors,
  InputErrorModel,
} from "@mindmemobile/mm-shared-global";
import { IAPIContactDetailModel, IAPICreateContactModel } from "../interfaces";

export class CreateContactModel {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  mobilePhoneCountry: CountryWithStateModel;
  mobilePhoneNumber: string;
  homePhoneCountry: CountryWithStateModel;
  homePhoneNumber: string;
  workPhoneCountry: CountryWithStateModel;
  workPhoneNumber: string;
  countryDetails: CountryWithStateModel;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  taxStatus: keyof typeof TaxStatus;
  taxCountryDetails: CountryWithStateModel;
  taxId: string;
  countries: CountryWithStateModel[];
  errors: InputErrorModel<CreateContactModel>[];

  constructor(data?: Partial<CreateContactModel>) {
    this.id = (data && data.id) || "";
    this.firstName = (data && data.firstName) || "";
    this.lastName = (data && data.lastName) || "";
    this.email = data?.email || "";
    this.company = data?.company || "";
    this.homePhoneCountry =
      (data && data.homePhoneCountry) || new CountryWithStateModel();
    this.workPhoneCountry =
      (data && data.workPhoneCountry) || new CountryWithStateModel();
    this.mobilePhoneCountry =
      (data && data.mobilePhoneCountry) || new CountryWithStateModel();
    this.homePhoneNumber = data?.homePhoneNumber || "";
    this.workPhoneNumber = data?.workPhoneNumber || "";
    this.mobilePhoneNumber = data?.mobilePhoneNumber || "";
    this.countryDetails = data?.countryDetails || new CountryWithStateModel();
    this.address1 = data?.address1 || "";
    this.address2 = data?.address2 || "";
    this.city = data?.city || "";
    this.state = data?.state || "";
    this.zip = data?.zip || "";
    this.taxStatus = data?.taxStatus || TaxStatus.Taxable;
    this.taxCountryDetails =
      data?.taxCountryDetails || new CountryWithStateModel();
    this.taxId = data?.taxId || "";
    this.countries = data?.countries || [];
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<CreateContactModel>(e);
        })) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  static deserializeAPIModel(data: IAPIContactDetailModel): CreateContactModel {
    const mappedData: Partial<CreateContactModel> = {
      id: data.id,
      firstName: data.firstName,
      lastName: data.lastName,
      company: data.company,
      mobilePhoneCountry:
        data.mobilePhone &&
        new CountryWithStateModel({
          code: parseInt(data.mobilePhone.countryCode),
          id: data.mobilePhone.countryId,
        }),
      mobilePhoneNumber:
        data.mobilePhone && data.mobilePhone.phoneNumber
          ? data.mobilePhone.phoneNumber
          : "",
      homePhoneCountry:
        data.homePhone &&
        new CountryWithStateModel({
          code: parseInt(data.homePhone.countryCode),
          id: data.homePhone.countryId,
        }),
      homePhoneNumber:
        data.homePhone && data.homePhone.phoneNumber
          ? data.homePhone.phoneNumber
          : "",
      workPhoneCountry:
        data.workPhone &&
        new CountryWithStateModel({
          code: parseInt(data.workPhone.countryCode),
          id: data.workPhone.countryId,
        }),
      workPhoneNumber:
        data.workPhone && data.workPhone.phoneNumber
          ? data.workPhone.phoneNumber
          : "",
      countryDetails: data.address
        ? new CountryWithStateModel({
            name: data.address.country,
            id: data.address.countryId || "",
          })
        : new CountryWithStateModel(),
      address1: data.address ? data.address.address1 : "",
      address2: data.address ? data.address.address2 : "",
      city: data.address ? data.address.city : "",
      state: data.address ? data.address.stateId || "" : "",
      zip: data.address ? data.address.zipCode : "",
      email: data.homeEmail || data.workEmail || data.otherEmail,
      taxId: data.taxId ? data.taxId : "",
      taxCountryDetails: data.taxCountryId
        ? new CountryWithStateModel({ id: data.taxCountryId })
        : new CountryWithStateModel(),
      taxStatus: data.taxStatus
        ? (data.taxStatus as keyof typeof TaxStatus)
        : TaxStatus.Taxable,
    };
    return new CreateContactModel(mappedData);
  }

  hasError(field: keyof CreateContactModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  errorMessage(field: keyof CreateContactModel): string | undefined {
    return this.errors.find((e) => e.field === field)?.message;
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<CreateContactModel>[] = [];
    CoreInputModelUtil.validateTextField(
      this.firstName,
      "firstName",
      newErrors
    );
    CoreInputModelUtil.validateTextField(this.lastName, "lastName", newErrors);
    if (this.email) {
      CoreInputModelUtil.validateEmail("email", this.email, newErrors);
    }

    // CoreInputModelUtil.validateTextField(this.company, "company", newErrors);
    // CoreInputModelUtil.validateTextField(this.address1, "address1", newErrors);
    // CoreInputModelUtil.validateTextField(this.address2, "address2", newErrors);
    // CoreInputModelUtil.validateTextField(this.city, "city", newErrors);
    // CoreInputModelUtil.validateTextField(this.state, "state", newErrors);
    // CoreInputModelUtil.validateTextField(this.zip, "zip", newErrors);

    if (this.mobilePhoneNumber) {
      CoreInputModelUtil.validateTextField(
        this.mobilePhoneCountry.name,
        "mobilePhoneCountry",
        newErrors
      );

      CoreInputModelUtil.validatePhoneNumber(
        "mobilePhoneNumber",
        this.mobilePhoneNumber,
        newErrors
      );
    }

    if (this.homePhoneNumber) {
      CoreInputModelUtil.validateTextField(
        this.homePhoneCountry.name,
        "homePhoneCountry",
        newErrors
      );
      CoreInputModelUtil.validatePhoneNumber(
        "homePhoneNumber",
        this.homePhoneNumber,
        newErrors
      );
    }
    if (this.workPhoneNumber) {
      CoreInputModelUtil.validateTextField(
        this.workPhoneCountry.name,
        "workPhoneCountry",
        newErrors
      );
      CoreInputModelUtil.validatePhoneNumber(
        "workPhoneNumber",
        this.workPhoneNumber,
        newErrors
      );
    }

    // CoreInputModelUtil.validateTextField(this.homePhoneCountry.name, 'homePhoneCountry', newErrors);
    // CoreInputModelUtil.validatePhoneNumber('homePhoneNumber', this.homePhoneNumber, newErrors);
    // CoreInputModelUtil.validateTextField(this.workPhoneCountry.name, 'workPhoneCountry', newErrors);
    // CoreInputModelUtil.validatePhoneNumber('workPhoneNumber', this.workPhoneNumber, newErrors);
    CoreInputModelUtil.validateTextField(
      this.countryDetails.name,
      "countryDetails",
      newErrors
    );
    CoreInputModelUtil.validateTextField(
      this.taxStatus,
      "taxStatus",
      newErrors
    );

    if (TaxStatus[this.taxStatus] === TaxStatus.TaxExempt) {
      CoreInputModelUtil.validateTaxId("taxId", this.taxId, newErrors);
    }
    CoreInputModelUtil.validateTextField(
      this.taxCountryDetails.name,
      "taxCountryDetails",
      newErrors
    );
    if (this.taxId) {
      CoreInputModelUtil.validateTaxId("taxId", this.taxId, newErrors);
    }

    if (!this.mobilePhoneNumber && !this.email) {
      newErrors.push(
        new InputErrorModel<CreateContactModel>({
          field: "mobilePhoneNumber",
          message: "Mobile Phone Number or Email is required",
        })
      );
    }

    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  createAPIModel(isFromFundraiser?: boolean): IAPICreateContactModel {
    return {
      id: this.id,
      email: this.email,
      mobilePhone: this.mobilePhoneNumber
        ? {
            countryCode: this.mobilePhoneCountry.code.toString(),
            countryId: this.mobilePhoneCountry.id,
            phoneNumber: this.mobilePhoneNumber,
          }
        : undefined,
      homePhone: this.homePhoneNumber
        ? {
            countryCode: this.homePhoneCountry.code.toString(),
            countryId: this.homePhoneCountry.id,
            phoneNumber: this.homePhoneNumber,
          }
        : undefined,
      workPhone: this.workPhoneNumber
        ? {
            countryCode: this.workPhoneCountry.code.toString(),
            countryId: this.workPhoneCountry.id,
            phoneNumber: this.workPhoneNumber,
          }
        : undefined,
      address: {
        country: this.countryDetails.name,
        countryId: this.countryDetails.id,
        city: this.city,
        zipCode: this.zip,
        countryCode: this.countryDetails.code.toString(),
        stateId: this.state,
        state: this.state,
        address1: this.address1,
        address2: this.address2,
        address3: "",
      },
      accountId: AuthService.authData.accountId,
      firstName: this.firstName,
      lastName: this.lastName,
      company: this.company,
      taxStatus: this.taxStatus,
      taxCountryId: this.taxCountryDetails.id,
      taxId: this.taxId,
      isFromFundraiser: isFromFundraiser === true,
    };
  }
}
